import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { IconButton } from '@doger/ui/components/ui/button';
import { $$toast } from '@doger/ui/components/ui/toast';
import { useAppForm } from '@doger/ui/form';
import { $$api } from '@doger/core/shared/api';

import styles from './sign-up-form.module.scss';

type SignUpFormProps = {
  className?: string;
  onSuccess: (params: { accessToken: string }) => void;
};

export const SignUpForm = ({ className, onSuccess }: SignUpFormProps) => {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation('core');

  const [showPassword, setShowPassword] = useState(false);

  const signUpSchema = z
    .object({
      firstName: z.string().min(1, t('validation.auth.firstNameRequired')),
      lastName: z.string().min(1, t('validation.auth.lastNameRequired')),
      email: z.email(t('validation.auth.emailInvalid')),
      password: z
        .string()
        .min(8, t('validation.auth.passwordMinLength', { min: 8 })),
      confirmPassword: z
        .string()
        .min(1, t('validation.auth.confirmPasswordRequired')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.auth.passwordsMismatch'),
      path: ['confirmPassword'],
    });

  const form = useAppForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },

    validators: {
      onSubmit: signUpSchema,
      onChange: signUpSchema,
    },

    onSubmit: async ({ value }) => {
      await signUp.mutateAsync(value);
    },
  });

  const signUp = useMutation({
    mutationKey: ['auth', 'signup', 'local'],
    mutationFn: async (data: z.infer<typeof signUpSchema>) => {
      const response = await $$api.register({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,

        language: i18n.language,
      });

      return response;
    },

    onSuccess: (data) => {
      $$api.tokensService.setTokens(data.accessToken, data.refreshToken);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
      onSuccess({ accessToken: data.accessToken });
    },

    onError: () => {
      $$toast.error(t('auth.toasts.signUpFailed'));
    },
  });

  return (
    <form
      name='sign-up-form'
      className={className}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <div className={styles.fields}>
        <div className={styles.nameFieldsGrid}>
          <form.AppForm>
            <form.AppField
              name='firstName'
              children={(field) => (
                <field.TextField
                  label={t('auth.forms.signUp.fields.firstName.label')}
                  variant='contained'
                  placeholder={t(
                    'auth.forms.signUp.fields.firstName.placeholder'
                  )}
                  autoComplete='given-name'
                />
              )}
            />
          </form.AppForm>

          <form.AppForm>
            <form.AppField
              name='lastName'
              children={(field) => (
                <field.TextField
                  label={t('auth.forms.signUp.fields.lastName.label')}
                  variant='contained'
                  placeholder={t(
                    'auth.forms.signUp.fields.lastName.placeholder'
                  )}
                  autoComplete='family-name'
                />
              )}
            />
          </form.AppForm>
        </div>

        <form.AppForm>
          <form.AppField
            name='email'
            children={(field) => (
              <field.TextField
                label={t('auth.forms.signUp.fields.email.label')}
                variant='contained'
                placeholder={t('auth.forms.signUp.fields.email.placeholder')}
                autoComplete='email'
              />
            )}
          />
        </form.AppForm>

        <form.AppForm>
          <form.AppField
            name='password'
            children={(field) => (
              <field.TextField
                label={t('auth.forms.signUp.fields.new-password.label')}
                variant='contained'
                type={showPassword ? 'text' : 'password'}
                placeholder={t(
                  'auth.forms.signUp.fields.new-password.placeholder'
                )}
                autoComplete='new-password'
                rightAdornment={
                  <IconButton
                    type='button'
                    variant='text'
                    size='md'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </IconButton>
                }
                data-clarity-mask='true'
              />
            )}
          />
        </form.AppForm>

        <form.AppForm>
          <form.AppField
            name='confirmPassword'
            children={(field) => (
              <field.TextField
                label={t('auth.forms.signUp.fields.confirmPassword.label')}
                variant='contained'
                placeholder={t(
                  'auth.forms.signUp.fields.confirmPassword.placeholder'
                )}
                type={showPassword ? 'text' : 'password'}
                autoComplete='new-password'
                data-clarity-mask='true'
              />
            )}
          />
        </form.AppForm>
      </div>

      <form.AppForm>
        <form.SubmitButton
          variant='contained'
          palette='primary'
          size='lg'
          children={({ isSubmitting }) =>
            isSubmitting
              ? t('auth.forms.signUp.actions.submitting')
              : t('auth.forms.signUp.actions.submit')
          }
        />
      </form.AppForm>
    </form>
  );
};
